/**
 * Emits design-token CSS custom properties (`--rr-*`) on a target element
 * (usually `<html>`). Re-runs when relevant slices of `useAppStore` change
 * so user a11y preferences (textScale / density / reducedMotion / highContrast)
 * and theme overrides (primary / secondary / tertiary / themeMode) propagate
 * instantly without React re-renders.
 */

import {
  palette,
  semantic,
  deriveSurfaceColors,
  type ColorShades
} from '@repletoryreach/sdk-utils/color';
import {
  spacingSemantic,
  typographyRoles,
  radius,
  shadow,
  zIndex,
  iconSize,
  thumbnailSize,
  duration,
  timing,
  fontFamily,
  fontWeight,
  lineHeight,
  letterSpacing,
  DENSITY_FACTORS,
  TEXT_SCALE_FACTORS,
  breakpoints,
  type SpacingTier
} from '@repletoryreach/sdk-utils/sizes';
import {
  MIN_TAP_TARGET_PX
} from '@repletoryreach/sdk-utils/a11y';
import type { Density, TextScale, ReducedMotion } from '@repletoryreach/sdk-utils/store';

export interface CssVarInputs {
  primary: string;
  secondary: string;
  tertiary: string;
  textScale: TextScale;
  density: Density;
  reducedMotion: ReducedMotion;
  reducedMotionOs: boolean;
  highContrast: boolean;
}

export interface AppliedCssVars {
  vars: Record<string, string>;
  attrs: Record<string, string>;
}

const PALETTE_SHADES: Array<keyof ColorShades> = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

function paletteVars(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [hue, shades] of Object.entries(palette)) {
    for (const shade of PALETTE_SHADES) {
      out[`--rr-color-${hue}-${shade}`] = (shades as ColorShades)[shade];
    }
  }
  return out;
}

function semanticVars(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(semantic)) {
    out[`--rr-color-${name}`] = value;
  }
  // Two names both consoles reach for that were never published. Every use was
  // either dead (`var(--rr-color-accent)` with no fallback resolves to nothing)
  // or carried a hardcoded hex fallback, which is how a colour escapes theming.
  // Publishing them as aliases fixes both at once.
  out['--rr-color-danger'] = out['--rr-color-error'];
  out['--rr-color-accent'] = 'var(--rr-color-primary)';
  return out;
}

/**
 * The two tints an attribute token (`{{contact.name}}`) and a Meta template
 * slot (`{{1}}`) are painted in wherever a console shows one: `TokenInput`,
 * `TokenTextarea` and `TokenText` in `inputs/TokenInput`.
 *
 * Both are mixed from aliases that `normalize.css` flips with `[data-theme]`
 * (`--rr-color-surface`, `--rr-color-text`), so one definition is right in
 * light and in dark without a second map here. An attribute is a brand tint,
 * because it is the thing the person chose; a slot is neutral, because it is
 * Meta's numbering and not something the person can change.
 */
function tokenChipVars(): Record<string, string> {
  return {
    '--rr-color-token-bg': 'color-mix(in srgb, var(--rr-color-primary) 16%, var(--rr-color-surface))',
    '--rr-color-token-fg': 'color-mix(in srgb, var(--rr-color-primary) 62%, var(--rr-color-text))',
    '--rr-color-token-border': 'color-mix(in srgb, var(--rr-color-primary) 34%, transparent)',
    '--rr-color-token-slot-bg': 'color-mix(in srgb, var(--rr-color-text) 11%, var(--rr-color-surface))',
    '--rr-color-token-slot-fg': 'var(--rr-color-text)',
    '--rr-color-token-slot-border': 'color-mix(in srgb, var(--rr-color-text) 22%, transparent)'
  };
}

function brandSurfaceVars(role: 'primary' | 'secondary' | 'tertiary', color: string): Record<string, string> {
  const s = deriveSurfaceColors({ color });
  return {
    [`--rr-color-${role}`]: color,
    [`--rr-color-${role}-text`]: s.text,
    [`--rr-color-${role}-text-muted`]: s.textMuted,
    [`--rr-color-${role}-text-subtle`]: s.textSubtle,
    [`--rr-color-${role}-border`]: s.border,
    [`--rr-color-${role}-hover`]: s.hover,
    [`--rr-color-${role}-active`]: s.active,
    [`--rr-color-${role}-ghost`]: s.ghost,
    [`--rr-color-${role}-focus-ring`]: s.focusRing,
    [`--rr-color-${role}-secondary`]: s.secondary,
    [`--rr-color-${role}-tertiary`]: s.tertiary
  };
}

function spacingVars(density: Density): Record<string, string> {
  const factor = DENSITY_FACTORS[density];
  const out: Record<string, string> = {};
  for (const [tier, base] of Object.entries(spacingSemantic) as Array<[SpacingTier, number]>) {
    out[`--rr-spacing-${tier}`] = `${Math.round(base * factor)}px`;
  }
  out['--rr-density-factor'] = String(factor);
  return out;
}

function radiusVars(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(radius)) {
    out[`--rr-radius-${k}`] = typeof v === 'number' ? `${v}px` : String(v);
  }
  return out;
}

function shadowVars(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(shadow)) {
    out[`--rr-shadow-${k}`] = String(v);
  }
  return out;
}

function zIndexVars(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(zIndex)) {
    out[`--rr-z-${k}`] = String(v);
  }
  return out;
}

function iconAndThumbVars(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(iconSize)) {
    out[`--rr-icon-${k}`] = `${v}px`;
  }
  for (const [k, v] of Object.entries(thumbnailSize)) {
    out[`--rr-thumb-${k}`] = `${v}px`;
  }
  return out;
}

function durationVars(reducedMotion: ReducedMotion, osReduce: boolean): Record<string, string> {
  const reduce = reducedMotion === 'always' || (reducedMotion === 'auto' && osReduce);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(duration)) {
    out[`--rr-duration-${k}`] = reduce ? '0ms' : `${v}ms`;
  }
  for (const [k, v] of Object.entries(timing)) {
    out[`--rr-easing-${k}`] = String(v);
  }
  out['--rr-motion-multiplier'] = reduce ? '0' : '1';
  return out;
}

function fontVars(): Record<string, string> {
  return {
    '--rr-font-system': fontFamily.system,
    '--rr-font-mono': fontFamily.mono,
    '--rr-fw-thin': String(fontWeight.thin),
    '--rr-fw-extralight': String(fontWeight.extralight),
    '--rr-fw-light': String(fontWeight.light),
    '--rr-fw-normal': String(fontWeight.normal),
    '--rr-fw-medium': String(fontWeight.medium),
    '--rr-fw-semibold': String(fontWeight.semibold),
    '--rr-fw-bold': String(fontWeight.bold),
    '--rr-fw-extrabold': String(fontWeight.extrabold),
    '--rr-fw-black': String(fontWeight.black),
    '--rr-lh-none': String(lineHeight.none),
    '--rr-lh-tight': String(lineHeight.tight),
    '--rr-lh-snug': String(lineHeight.snug),
    '--rr-lh-normal': String(lineHeight.normal),
    '--rr-lh-relaxed': String(lineHeight.relaxed),
    '--rr-lh-loose': String(lineHeight.loose),
    '--rr-ls-tighter': letterSpacing.tighter,
    '--rr-ls-tight': letterSpacing.tight,
    '--rr-ls-normal': letterSpacing.normal,
    '--rr-ls-wide': letterSpacing.wide,
    '--rr-ls-wider': letterSpacing.wider,
    '--rr-ls-widest': letterSpacing.widest
  };
}

function typographyVars(textScale: TextScale): Record<string, string> {
  const factor = TEXT_SCALE_FACTORS[textScale];
  const out: Record<string, string> = {};
  for (const [role, bundle] of Object.entries(typographyRoles)) {
    out[`--rr-fs-${role}`] = `${Math.round(bundle.fontSize * factor)}px`;
    out[`--rr-lh-${role}`] = String(bundle.lineHeight);
    out[`--rr-fw-${role}`] = String(bundle.fontWeight);
    out[`--rr-ls-${role}`] = bundle.letterSpacing;
  }
  out['--rr-text-scale-factor'] = String(factor);
  return out;
}

function breakpointVars(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(breakpoints)) {
    out[`--rr-bp-${k}`] = `${v}px`;
  }
  return out;
}

function a11yVars(highContrast: boolean): Record<string, string> {
  return {
    '--rr-tap-target': `${MIN_TAP_TARGET_PX}px`,
    '--rr-focus-ring-width': highContrast ? '3px' : '2px',
    '--rr-border-alpha': highContrast ? '0.8' : '0.45',
    '--rr-ghost-alpha': highContrast ? '0.16' : '0.08',
    '--rr-text-on-surface': highContrast ? '#000000' : '#0f172a',
    '--rr-text-on-surface-inverse': highContrast ? '#ffffff' : '#f8fafc'
  };
}

/** Compute the full CSS-variable map for one snapshot of app state. */
export function computeCssVars(inputs: CssVarInputs): AppliedCssVars {
  const vars: Record<string, string> = {
    ...paletteVars(),
    ...semanticVars(),
    ...brandSurfaceVars('primary', inputs.primary),
    ...brandSurfaceVars('secondary', inputs.secondary),
    ...brandSurfaceVars('tertiary', inputs.tertiary),
    ...tokenChipVars(),
    ...spacingVars(inputs.density),
    ...radiusVars(),
    ...shadowVars(),
    ...zIndexVars(),
    ...iconAndThumbVars(),
    ...durationVars(inputs.reducedMotion, inputs.reducedMotionOs),
    ...fontVars(),
    ...typographyVars(inputs.textScale),
    ...breakpointVars(),
    ...a11yVars(inputs.highContrast)
  };
  return { vars, attrs: {} };
}

/** Imperatively write all variables onto an element's inline style. */
export function applyCssVars(target: HTMLElement, vars: Record<string, string>): void {
  for (const [k, v] of Object.entries(vars)) {
    target.style.setProperty(k, v);
  }
}

/** Compare two var maps and write only the changed entries. */
export function diffApplyCssVars(
  target: HTMLElement,
  next: Record<string, string>,
  prev: Record<string, string> | null
): void {
  if (!prev) return applyCssVars(target, next);
  for (const [k, v] of Object.entries(next)) {
    if (prev[k] !== v) target.style.setProperty(k, v);
  }
}
